/**
 * Session-token plumbing for the native apps' WebView viewers.
 *
 * Inside the app's WebView the Better Auth session lives in SecureStore, so the
 * page carries no cookie. The shell injects the token via the native bridge
 * (`getNativeToken`); these helpers turn it into the two things the viewer
 * needs to be recognised by the server:
 *   - an in-band WebSocket `authenticate` message (never a `?token=` URL —
 *     request URLs are logged in full), sent before `subscribe`;
 *   - an `Authorization: Bearer` header on HTTP reads, but only for our own API.
 */
import { AUTHENTICATE_TOKEN_MAX_LENGTH, MessageType } from './protocol.js';
import { classifyProxyResponse } from './proxy-errors.js';
import { isSameApiOrigin } from './url-validation.js';
/** The `authenticate` frame for `token`, or `null` when there is nothing valid to send. */
export function buildAuthenticateMessage(token) {
    if (!token || token.length > AUTHENTICATE_TOKEN_MAX_LENGTH)
        return null;
    return { type: MessageType.AUTHENTICATE, token };
}
/**
 * `headers` plus the bearer token when `url` is on our own API origin. Any other
 * host (LAN source, cross-origin) never sees the token.
 */
export function withNativeBearer(url, apiUrl, token, headers) {
    const result = new Headers(headers);
    if (token && isSameApiOrigin(url, apiUrl)) {
        result.set('Authorization', `Bearer ${token}`);
    }
    return result;
}
/** A bearer image fetch the server refused, with what the refusal said. */
export class BearerImageError extends Error {
    outcome;
    constructor(status, outcome) {
        super(`HTTP ${status}`);
        this.name = 'BearerImageError';
        this.outcome = outcome;
    }
}
/**
 * `url` without the per-tick `t` cache-buster. A bearer fetch is `no-store` and
 * so does not need it, while a CORS preflight is cached per URL: with a new `t`
 * every frame paid an extra OPTIONS round trip, whatever `Access-Control-Max-Age`
 * said (prod, 16. 10. 2026: 71 at 1 Hz from one client).
 */
export function withoutCacheBuster(url) {
    return url.replace(/([?&])t=\d+(&|$)/, (_match, before, after) => after ? before : '');
}
/**
 * Fetch an image frame with the bearer token, for the app WebView where an
 * `<img src>` carries no cookie and no header (MU 7. 10. 2026: the owner of an
 * IP-restricted space, signed in to the app outside the range, got 403 on every
 * frame). Returns `null` when the bearer does not apply — no token, or not our
 * API origin — so the caller keeps the plain URL. Throws on a non-2xx response.
 * Redirects are refused so the header never follows one to another host.
 */
export async function fetchBearerImage(url, apiUrl, token, init = {}) {
    if (!token || !isSameApiOrigin(url, apiUrl))
        return null;
    const doFetch = init.fetchImpl ?? fetch;
    const response = await doFetch(url, {
        headers: withNativeBearer(url, apiUrl, token),
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
        signal: init.signal,
    });
    if (!response.ok) {
        throw new BearerImageError(response.status, classifyProxyResponse({
            ok: false,
            status: response.status,
            body: await response.json().catch(() => null),
            retryAfter: response.headers.get('Retry-After'),
        }));
    }
    return response.blob();
}
//# sourceMappingURL=native-auth.js.map